import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Specialist } from '../models/specialist.model';
import { Service } from '../models/service.model';


@Injectable({
  providedIn: 'root'
})
export class CrmService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly specialistsEndpoint = `/api/crm/specialists`;
  private readonly servicesEndpoint    = `${environment.apiBaseUrl}/api/crm/services`;
  private readonly leadsEndpoint        = `${environment.apiBaseUrl}/api/crm/leads`;
  private readonly eventsEndpoint       = `${environment.apiBaseUrl}/api/crm/events`;
  private readonly availabilityEndpoint = `${environment.apiBaseUrl}/api/crm/availability`;
  private readonly appointmentsEndpoint = `${environment.apiBaseUrl}/api/crm/appointments`;
  private readonly dealsEndpoint        = `${environment.apiBaseUrl}/api/crm/deals`;
  private readonly estimateEndpoint     = `${environment.apiBaseUrl}/api/crm/estimate`;

  createDeal(dealData: any): Observable<{ success: boolean; dealId: string; mrr: number; acv: number; stage: string }> {
    return this.http.post<any>(this.dealsEndpoint, dealData);
  }

  getEstimate(estimateParams: any): Observable<any> {
    return this.http.post<any>(this.estimateEndpoint, estimateParams);
  }

  submitLead(customerData: any): Observable<any> {
    return this.http.post(this.leadsEndpoint, customerData);
  }

  requestConsultation(consultationData: any): Observable<any> {
    return this.http.post(this.leadsEndpoint, {
      ...consultationData,
      leadSource: consultationData.leadSource || 'Senior Independence Consultation',
    });
  }

  createEvent(eventData: any): Observable<any> {
    return this.http.post(this.eventsEndpoint, eventData);
  }

  getAvailability(specialistId: string, date: string): Observable<string[]> {
    const params = new HttpParams()
      .set('specialistId', specialistId)
      .set('date', date);
    return this.http.get<string[]>(this.availabilityEndpoint, { params });
  }

  bookAppointment(bookingData: any): Observable<any> {
    return this.http.post(this.appointmentsEndpoint, bookingData);
  }

  getMyAppointments(): Observable<any[]> {
    return this.http.get<any[]>(`${this.appointmentsEndpoint}/me`);
  }

  syncCustomerProfile(profileId: string): void {
    console.log('Syncing customer profile from CRM:', profileId);
    // TODO: wire up to CRM profile sync endpoint
  }

  syncServices(): Observable<Service[]> {
    const MOCK_SERVICES: Service[] = [
      { id: '7401137000000697502', name: 'Cleaning Studio', description: 'Experience meticulous cleaning tailored for studio residences.', location: 'Client Address', price: 190 },
      { id: '7401137000000723010', name: 'Cleaning 1 Bedroom', description: 'Experience meticulous cleaning tailored for one-bedroom homes.', location: 'Client Address', price: 222 },
    ];
    if (!isPlatformBrowser(this.platformId)) {
      return of(MOCK_SERVICES);
    }
    return this.http.get<Service[]>(this.servicesEndpoint).pipe(
      map(services => services && services.length > 0 ? services : MOCK_SERVICES),
      catchError(() => of(MOCK_SERVICES))
    );
  }

  syncSpecialistProfiles(): Observable<Specialist[]> {
    if (!isPlatformBrowser(this.platformId)) {
      return of([]);
    }
    return this.http.get<Specialist[]>(this.specialistsEndpoint);
  }
}
